import type { PortalShellLabels } from '@whinerp/ui';
import type { useT } from '../../app/i18n.js';

/**
 * The shell's strings, built once for all three surfaces.
 *
 * Three callers building this three times would be three places for one of
 * them to drift - and the string that drifts would be a refusal page, which is
 * the one a customer reads when something has already gone wrong.
 */
export function portalShellLabels(
  t: ReturnType<typeof useT>,
  workspaceName: string | null,
): PortalShellLabels {
  return {
    /*
     * Empty when there is no workspace name, and the shell then draws no
     * footer at all.
     *
     * "Sent by " with nothing after it is the obvious bug. The less obvious
     * one is that a refusal page must not name the workspace even when it
     * could: an invalid token confirming that a workspace exists at this
     * address is the difference between a dead link and a probe that returns
     * something useful.
     */
    poweredBy: workspaceName ? t('portal.poweredBy', { values: { workspace: workspaceName } }) : '',
    content: t('portal.content'),
    requestNewLink: t('portal.requestNewLink'),
    refusal: {
      invalid: {
        title: t('portal.refusal.invalid.title'),
        body: t('portal.refusal.invalid.body'),
      },
      expired: {
        title: t('portal.refusal.expired.title'),
        body: t('portal.refusal.expired.body'),
      },
      revoked: {
        title: t('portal.refusal.revoked.title'),
        body: t('portal.refusal.revoked.body'),
      },
      'wrong-surface': {
        title: t('portal.refusal.wrong-surface.title'),
        body: t('portal.refusal.wrong-surface.body'),
      },
    },
  };
}

/**
 * A field key, in words.
 *
 * The server sends column names because that is what the allowlist stores. A
 * key with no translation shows as itself rather than as a blank: a customer
 * seeing `valid_until` knows something is wrong, and a customer seeing nothing
 * does not.
 */
export function portalFieldLabel(t: ReturnType<typeof useT>, key: string): string {
  const known = new Set([
    'full_name',
    'email',
    'title',
    'number',
    'status',
    'total',
    'currency',
    'valid_until',
    'name',
    'amount',
    'close_date',
    'protected_until',
  ]);
  return known.has(key) ? t(`portal.field.${key}` as 'portal.field.email') : key;
}
